import React, { useState } from "react";
import { url } from "../lib/api";
import ChatUI from "../components/ChatUI";
import NutritionDashboard from "../components/NutritionDashboard";
import GameSection from "../components/GameSection";
import { motion } from "framer-motion";
import AuthForm from "../components/AuthForm";
import ProfileEdit from "../components/ProfileEdit";
import { useEffect } from "react";

const Home = () => {
  const [nutrition, setNutrition] = useState(null);
  const [mealPlan, setMealPlan] = useState([]);
  const [groceryList, setGroceryList] = useState([]);
  const [form, setForm] = useState({
    age: "",
    gender: "",
    height: "",
    weight: "",
    activity: "",
    goal: "",
    allergies: "",
    preferences: "Pakistani",
    days: 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeSection, setActiveSection] = useState("chat"); // 'chat', 'meal', 'games', 'profile'
  // Auth state
  const [token, setToken] = useState(localStorage.getItem("jwt_token") || "");
  const [profile, setProfile] = useState(() => {
    const p = localStorage.getItem("user_profile");
    return p ? JSON.parse(p) : null;
  });

  // Handle form input changes
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Submit nutrition info to backend
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(url("/v1/plan/generate/"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });
      const text = await res.text();
      let data = null;
      try {
        data = JSON.parse(text || "{}");
      } catch (e) {
        // non-JSON response
      }
      if (!res.ok) {
        const serverMsg =
          (data && (data.detail || data.error || data.msg)) ||
          text ||
          `HTTP ${res.status} ${res.statusText}`;
        // Surface a detailed error message (status + server body) instead
        // of a vague "Failed to fetch" so users know whether it was a
        // validation issue or a connectivity/CORS problem.
        setError(`Error: ${serverMsg}`);
        setLoading(false);
        return;
      }
      setNutrition(data && data.nutrition);
      setMealPlan(data && data.meal_plan);
      setGroceryList(data && data.grocery_list);
    } catch (err) {
      setError(err.message || "Unable to connect to backend or invalid data.");
    }
    setLoading(false);
  };

  // Profile features removed

  // If not logged in, show AuthForm
  const handleLogout = () => {
    setToken("");
    setProfile(null);
    localStorage.removeItem("jwt_token");
    localStorage.removeItem("user_profile");
  };

  if (!token) {
    return <AuthForm setToken={setToken} setProfile={setProfile} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-200 via-blue-100 to-blue-300 flex flex-col items-center justify-center py-6 md:py-10">
      <div className="w-full max-w-3xl mx-auto px-2 md:px-0">
        <div className="flex justify-end items-center mb-2 space-x-2">
          {/* Desktop logout button */}
          <button
            onClick={handleLogout}
            className="hidden md:inline-block bg-red-500 text-white px-4 py-2 rounded-lg font-bold hover:bg-red-600 transition"
          >
            Logout
          </button>

          {/* Mobile menu button (hamburger) - visible on small screens */}
          <div className="relative md:hidden">
            <MobileMenuButton
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              handleLogout={handleLogout}
            />
          </div>
        </div>
        {/* Section Switcher Buttons (hidden on small screens; use mobile menu) */}
        <div className="hidden md:flex justify-center gap-4 mb-8">
          <button
            className={`px-6 py-3 rounded-xl font-bold shadow transition-all text-lg focus:outline-none ${
              activeSection === "chat"
                ? "bg-blue-500 text-white scale-105"
                : "bg-white text-blue-700 border border-blue-300"
            }`}
            onClick={() => setActiveSection("chat")}
          >
            Chat
          </button>
          <button
            className={`px-6 py-3 rounded-xl font-bold shadow transition-all text-lg focus:outline-none ${
              activeSection === "meal"
                ? "bg-green-500 text-white scale-105"
                : "bg-white text-green-700 border border-green-300"
            }`}
            onClick={() => setActiveSection("meal")}
          >
            Generate Meal Plan
          </button>
          <button
            className={`px-6 py-3 rounded-xl font-bold shadow transition-all text-lg focus:outline-none ${
              activeSection === "games"
                ? "bg-yellow-400 text-white scale-105"
                : "bg-white text-yellow-700 border border-yellow-300"
            }`}
            onClick={() => setActiveSection("games")}
          >
            Diet Games
          </button>
          <button
            className={`px-6 py-3 rounded-xl font-bold shadow transition-all text-lg focus:outline-none ${
              activeSection === "profile"
                ? "bg-purple-500 text-white scale-105"
                : "bg-white text-purple-700 border border-purple-300"
            }`}
            onClick={() => setActiveSection("profile")}
          >
            Edit Profile
          </button>
        </div>
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-8"
        >
          <h1 className="text-5xl font-extrabold mb-2 text-center text-blue-700 drop-shadow-lg tracking-tight">
            Nutrition Chatbot
          </h1>
          <p className="text-lg text-center text-gray-700 font-medium mb-4">
            Your personal AI nutritionist for healthy Pakistani meal plans
          </p>
        </motion.div>

        {/* Chat Section */}
        {activeSection === "chat" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="rounded-2xl shadow-2xl bg-white/80 p-4 md:p-6 mb-8"
          >
            <ChatUI token={token} profile={profile} />
          </motion.div>
        )}

        {/* Profile Edit Section */}
        {activeSection === "profile" && (
          <ProfileEdit
            profile={profile}
            token={token}
            setProfile={setProfile}
          />
        )}

        {/* Meal Plan Section */}
        {activeSection === "meal" && (
          <>
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              onSubmit={handleSubmit}
              className="bg-white/90 rounded-2xl shadow-xl p-4 md:p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6"
            >
              {/* ...existing form code... */}
              <div>
                <label className="block font-bold text-blue-700 mb-1">
                  Age
                </label>
                <input
                  type="number"
                  name="age"
                  value={form.age}
                  onChange={handleChange}
                  required
                  min="1"
                  className="w-full px-4 py-2 rounded-lg border-2 border-blue-200 focus:outline-none focus:ring focus:ring-blue-100"
                />
              </div>
              <div>
                <label className="block font-bold text-blue-700 mb-1">
                  Gender
                </label>
                <select
                  name="gender"
                  value={form.gender}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border-2 border-blue-200 focus:outline-none focus:ring focus:ring-blue-100"
                >
                  <option value="">Select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
              <div>
                <label className="block font-bold text-blue-700 mb-1">
                  Height (cm)
                </label>
                <input
                  type="number"
                  name="height"
                  value={form.height}
                  onChange={handleChange}
                  required
                  min="50"
                  className="w-full px-4 py-2 rounded-lg border-2 border-blue-200 focus:outline-none focus:ring focus:ring-blue-100"
                />
              </div>
              <div>
                <label className="block font-bold text-blue-700 mb-1">
                  Weight (kg)
                </label>
                <input
                  type="number"
                  name="weight"
                  value={form.weight}
                  onChange={handleChange}
                  required
                  min="20"
                  className="w-full px-4 py-2 rounded-lg border-2 border-blue-200 focus:outline-none focus:ring focus:ring-blue-100"
                />
              </div>
              <div>
                <label className="block font-bold text-blue-700 mb-1">
                  Activity Level
                </label>
                <select
                  name="activity"
                  value={form.activity}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border-2 border-blue-200 focus:outline-none focus:ring focus:ring-blue-100"
                >
                  <option value="">Select</option>
                  <option value="sedentary">Sedentary</option>
                  <option value="light">Lightly Active</option>
                  <option value="moderate">Moderately Active</option>
                  <option value="active">Active</option>
                  <option value="very">Very Active</option>
                </select>
              </div>
              <div>
                <label className="block font-bold text-blue-700 mb-1">
                  Goal
                </label>
                <select
                  name="goal"
                  value={form.goal}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border-2 border-blue-200 focus:outline-none focus:ring focus:ring-blue-100"
                >
                  <option value="">Select</option>
                  <option value="lose">Lose Weight</option>
                  <option value="maintain">Maintain Weight</option>
                  <option value="gain">Gain Weight</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block font-bold text-blue-700 mb-1">
                  Allergies
                </label>
                <input
                  type="text"
                  name="allergies"
                  value={form.allergies}
                  onChange={handleChange}
                  placeholder="e.g. nuts, dairy"
                  className="w-full px-4 py-2 rounded-lg border-2 border-blue-200 focus:outline-none focus:ring focus:ring-blue-100"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block font-bold text-blue-700 mb-1">
                  Dietary Preferences
                </label>
                <input
                  type="text"
                  name="preferences"
                  value={form.preferences}
                  onChange={handleChange}
                  placeholder="Pakistani, vegetarian, etc."
                  className="w-full px-4 py-2 rounded-lg border-2 border-blue-200 focus:outline-none focus:ring focus:ring-blue-100"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block font-bold text-blue-700 mb-1">
                  Days
                </label>
                <select
                  name="days"
                  value={form.days}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border-2 border-blue-200 focus:outline-none focus:ring focus:ring-blue-100"
                >
                  {[...Array(14)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1} Day{i === 0 ? "" : "s"}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2 flex flex-col md:flex-row justify-end items-center gap-2 md:gap-4 mt-2">
                {error && (
                  <span className="text-red-500 font-semibold mb-2 md:mb-0">
                    {error}
                  </span>
                )}
                <button
                  type="submit"
                  className="w-full md:w-auto bg-gradient-to-r from-green-400 to-blue-500 text-white px-6 md:px-8 py-3 rounded-xl font-bold shadow hover:scale-105 transition-transform"
                  disabled={loading}
                >
                  {loading ? "Generating..." : "Generate Meal Plan"}
                </button>
              </div>
            </motion.form>
            {/* Nutrition Dashboard Section */}
            {nutrition && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.3 }}
              >
                <NutritionDashboard
                  calories={nutrition.calories}
                  macros={Object.entries(nutrition.macros).map(
                    ([name, value]) => ({
                      name,
                      value,
                    })
                  )}
                  mealPlan={mealPlan}
                  groceryList={groceryList}
                />
              </motion.div>
            )}
          </>
        )}

        {/* Games Section */}
        {activeSection === "games" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="rounded-2xl shadow-2xl bg-white/80 p-4 md:p-6 mb-8"
          >
            <GameSection />
          </motion.div>
        )}
      </div>
      {/* Footer */}
      <footer className="mt-6 md:mt-10 text-center text-gray-500 text-xs md:text-sm px-2">
        Made by Ahmed Rasheed Group
      </footer>
    </div>
  );
};

export default Home;

// Mobile menu button component (kept in the same file for simplicity)
function MobileMenuButton({ activeSection, setActiveSection, handleLogout }) {
  const [open, setOpen] = React.useState(false);

  // close on escape or when switching to desktop
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="relative">
      <button
        aria-label="Open menu"
        onClick={() => setOpen((s) => !s)}
        className="p-2 rounded-lg bg-white/90 border border-blue-200 shadow hover:scale-105 transition"
      >
        <svg
          className="w-6 h-6 text-blue-700"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <nav className="flex flex-col">
            <MenuItem
              label="Chat"
              active={activeSection === "chat"}
              onClick={() => {
                setActiveSection("chat");
                setOpen(false);
              }}
            />
            <MenuItem
              label="Generate Meal Plan"
              active={activeSection === "meal"}
              onClick={() => {
                setActiveSection("meal");
                setOpen(false);
              }}
            />
            <MenuItem
              label="Diet Games"
              active={activeSection === "games"}
              onClick={() => {
                setActiveSection("games");
                setOpen(false);
              }}
            />
            <MenuItem
              label="Edit Profile"
              active={activeSection === "profile"}
              onClick={() => {
                setActiveSection("profile");
                setOpen(false);
              }}
            />
            <button
              onClick={() => {
                setOpen(false);
                handleLogout();
              }}
              className="text-left px-4 py-2 hover:bg-red-50 border-t text-red-600 font-semibold rounded-b-lg"
            >
              Logout
            </button>
          </nav>
        </div>
      )}
    </div>
  );
}

function MenuItem({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-left hover:bg-blue-50 ${
        active ? "bg-blue-100 font-bold" : ""
      }`}
    >
      {label}
    </button>
  );
}
